import "./styles.css";

import {
  Button,
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import { RcFile } from "antd/lib/upload";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { UploadOutlined } from "@ant-design/icons";

import { useAppDispatch } from "../../../app/hooks";
import { updateAdminListValue } from "../../../features/admin/adminListSlice";
import { postAdminAsync } from "../../../features/admin/postAdminSlice";
import { putAdminAsync } from "../../../features/admin/putAdminSlice";
import { updatePartnerPriceTableAsync } from "../../../features/admin/updatePartnerPriceTableSlice";
import { AdminTypesConstants } from "../../../shared/constants/adminTypesConstants";
import {
  AdminCreate,
  AdminTypeIdDescription,
  AdminUpdate,
  EmailDescription,
  IdDescription,
  MinimumTripPriceDescription,
  MobileDescription,
  NameDescription,
  PartnerDescription,
  PasswordDescription,
  PasswordRegexExpression,
  PasswordRequirementText,
  PictureDescription,
  PriceTableDescription,
  CustomVoucherLogosDescription,
  InternationalDescription,
  MarkupDescription,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import AdminDTO from "../../../shared/dtos/adminDTO";
import NewAdminDTO from "../../../shared/dtos/newAdminDTO";
import { selectCurrentAdminToken } from "../../../utils/helpers/selectorHelper";
import ContainerLayout from "../../common/containerLayout";
import { getAdmin, getCountries } from "../../../services/adminApi";
import {
  DEFAULT_ADMIN_MARKUP,
  getAdminPricingPayload,
  normalizeAdminPricing,
} from "./utils/pricing";

const { Title, Text } = Typography;
const apiUrl = process.env.REACT_APP_API_URL;

const isJpgOrPng = (file: File) =>
  file.type === "image/jpeg" || file.type === "image/png";

const wrongFileNotification = () =>
  notification.error({
    message: "Solo se aceptan archivos con formato JPG y PNG",
    placement: "bottom",
    key: "global",
  });

export const AdminForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  let { adminId } = useParams();
  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    getCountries().then(setCountries);
  }, []);

  const uploadProps: UploadProps = {
    listType: "picture",
    maxCount: 1,
    headers: {
      auth: selectCurrentAdminToken(),
    },
    beforeUpload: (file: RcFile) => beforeUpload(file),
    accept: ".jpeg,.png",
    action: `${apiUrl}files/upload/vouchers`,
  };

  useEffect(() => {
    const checkIfAdminId = async () => {
      if (!adminId) {
        setIsLoading(false);
        return;
      }

      const {
        id,
        name,
        email,
        mobile,
        minimumTripPrice,
        partner,
        picture,
        adminTypeId,
        priceTable,
        customVoucherLogos,
        international,
        markup,
        countryId,
      }: AdminDTO = await getAdmin(+adminId);

      const pricingFields = normalizeAdminPricing(international, markup);

      form.setFields([
        {
          name: [IdDescription],
          value: id ?? null,
        },
        {
          name: [NameDescription],
          value: name ?? null,
        },
        {
          name: [EmailDescription],
          value: email ?? null,
        },
        {
          name: [MobileDescription],
          value: mobile ?? null,
        },
        {
          name: [MinimumTripPriceDescription],
          value: minimumTripPrice ?? null,
        },
        {
          name: [PartnerDescription],
          value: partner ?? null,
        },
        {
          name: [PictureDescription],
          value: picture ?? null,
        },
        {
          name: [AdminTypeIdDescription],
          value: adminTypeId ?? null,
        },
        {
          name: [PriceTableDescription],
          value: priceTable ?? null,
        },
        {
          name: [CustomVoucherLogosDescription],
          value: customVoucherLogos ?? null,
        },
        {
          name: [InternationalDescription],
          value: pricingFields.international,
        },
        {
          name: [MarkupDescription],
          value: pricingFields.markup,
        },
        {
          name: "countryId",
          value: countryId ?? null,
        },
      ]);
      setIsLoading(false);
    };
    checkIfAdminId();
  }, [form, adminId]);

  const onFinish = async (values: any) => {
    const adminId = form.getFieldValue(IdDescription);
    const {
      name,
      email,
      mobile,
      adminTypeId,
      password,
      picture,
      minimumTripPrice,
      customVoucherLogos,
      international,
      markup,
      countryId,
    } = values;
    const fileUrl = picture?.file?.response?.location;
    const customVoucherLogosUrl = customVoucherLogos?.file?.response?.location;
    const pricingPayload = getAdminPricingPayload(international, markup);

    if (
      !name ||
      !email ||
      !mobile ||
      !adminTypeId ||
      (!adminId && !password.length)
    )
      return;

    const newAdminDTO: NewAdminDTO = {
      name,
      email,
      mobile,
      active: true,
      adminTypeId,
      password,
      ...(minimumTripPrice ? { minimumTripPrice: +minimumTripPrice } : {}),
      ...(fileUrl ? { picture: fileUrl } : {}),
      ...(customVoucherLogosUrl ? { customVoucherLogos: customVoucherLogosUrl } : {}),
      ...(countryId ? { countryId: +countryId } : {}),
      ...pricingPayload,
    };

    setIsLoading(true);

    try {
      if (adminId) {
        await dispatch(putAdminAsync({ id: adminId, newAdminDTO }));
      } else {
        await dispatch(postAdminAsync(newAdminDTO));
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }

    form.resetFields();
    dispatch(updateAdminListValue());
    navigate(WebRoutes.AdminTableRoute, {
      replace: true,
    });
  };

  const onUpdatePriceTable = async () => {
    const adminId = form.getFieldValue(IdDescription);
    const picture = form.getFieldValue(PriceTableDescription);
    const fileUrl = picture.file.response.location;

    if (!fileUrl || !adminId) return;

    setIsLoading(true);
    try {
      await dispatch(updatePartnerPriceTableAsync({ id: adminId, fileUrl }));
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const beforeUpload = async (file: RcFile) => {
    if (!isJpgOrPng(file)) {
      wrongFileNotification();
      return false;
    }
    return true;
  };

  return (
    <ContainerLayout
      title={
        form.getFieldValue(IdDescription)
          ? `Administrador: ${form.getFieldValue(NameDescription)}`
          : "Administrador"
      }
      showNotification={false}
      justify={form.getFieldValue(PartnerDescription) ? "start" : "center"}
    >
      <Col span={!form.getFieldValue(PartnerDescription) ? 12 : 24}>
        <Form
          layout="vertical"
          form={form}
          name="admin"
          onFinish={onFinish}
          scrollToFirstError
          initialValues={{
            [InternationalDescription]: false,
            [MarkupDescription]: DEFAULT_ADMIN_MARKUP,
          }}
        >
          <Row gutter={10}>
            <Col span={!form.getFieldValue(PartnerDescription) ? 24 : 12}>
              <Form.Item>
                <Title level={2}>Información del administrador</Title>
              </Form.Item>
              <Form.Item
                valuePropName={PictureDescription}
                name={PictureDescription}
                label="Logo del administrador"
                tooltip="Elige una imagen que represente al administrador"
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Seleccionar imagen</Button>
                </Upload>
              </Form.Item>
              {form.getFieldValue(PictureDescription) && (
                <Form.Item>
                  <Row justify="center">
                    <Image
                      src={form.getFieldValue(PictureDescription)}
                      style={{ maxWidth: "300px", maxHeight: "100px" }}
                      alt="avatar"
                      preview={false}
                    />
                  </Row>
                </Form.Item>
              )}
              <Form.Item
                valuePropName={CustomVoucherLogosDescription}
                name={CustomVoucherLogosDescription}
                label="Logo del voucher"
                tooltip="Elige un logo personalizado para los vouchers"
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Seleccionar imagen</Button>
                </Upload>
              </Form.Item>
              {form.getFieldValue(CustomVoucherLogosDescription) && (
                <Form.Item>
                  <Row justify="center">
                    <Image
                      src={form.getFieldValue(CustomVoucherLogosDescription)}
                      style={{ maxWidth: "300px", maxHeight: "100px" }}
                      alt="voucher logo"
                      preview={false}
                    />
                  </Row>
                </Form.Item>
              )}
              <Form.Item
                name={NameDescription}
                label="Nombre"
                tooltip="Cual es su nombre?"
                rules={[
                  {
                    required: true,
                    message: "Por favor inserte su nombre!",
                    whitespace: true,
                  },
                ]}
              >
                <Input placeholder="Pepe" />
              </Form.Item>
              <Form.Item
                name={EmailDescription}
                label="Correo electronico"
                tooltip="Cual es su correo electronico?"
                rules={[
                  {
                    type: EmailDescription,
                    message: "Inserte un correo válido!",
                  },
                  {
                    required: true,
                    message: "Por favor inserte su correo electronico!",
                  },
                ]}
              >
                <Input placeholder="prueba@mowi.com" />
              </Form.Item>
              <Form.Item
                name={MobileDescription}
                label="Teléfono"
                tooltip="Cual es su teléfono?"
                rules={[
                  {
                    required: true,
                    message: "Por favor inserte su número de teléfono!",
                  },
                ]}
              >
                <Input placeholder="52013994" />
              </Form.Item>
              <Form.Item
                name={MinimumTripPriceDescription}
                label="Precio mínimo por viaje"
                tooltip="Cual es el precio mínimo por viaje? (Si no ingresa un valor se asignará nulo por defecto)"
              >
                <Input prefix="$" type="number" placeholder="100" />
              </Form.Item>
              <Form.Item
                name={AdminTypeIdDescription}
                label="Tipo de administrador"
                tooltip="Que tipo de administrador quieres elegir?"
                rules={[
                  {
                    required: true,
                    message: "Por favor seleccione un tipo de administrador!",
                  },
                ]}
              >
                <Select
                  placeholder="Selecione un tipo"
                  showSearch
                  optionFilterProp="label"
                  options={AdminTypesConstants}
                />
              </Form.Item>
              <Form.Item
                name="countryId"
                label="País"
                tooltip="País base del administrador para detección de viajes internacionales"
              >
                <Select
                  placeholder="Seleccione un país"
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label as string ?? '')
                      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .includes(
                        input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                      )
                  }
                  options={countries.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name={InternationalDescription}
                label="Precios internacionales"
                tooltip="Cuando está activado, este porcentaje se agrega al precio final del viaje para este administrador."
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item shouldUpdate={(prev, current) =>
                prev[InternationalDescription] !== current[InternationalDescription]
              }>
                {() => {
                  const internationalEnabled = form.getFieldValue(
                    InternationalDescription,
                  );
                  return (
                    <Form.Item
                      name={MarkupDescription}
                      label="Margen de ganancia (%)"
                      tooltip="Cuando está activado, este porcentaje se agrega al precio final del viaje para este administrador."
                      rules={[
                        {
                          type: "number",
                          min: 0,
                          message: "El margen de ganancia debe ser un numero mayor o igual a 0",
                        },
                      ]}
                      extra="El margen de ganancia se aplica al precio final del viaje."
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{
                          width: "100%",
                          opacity: internationalEnabled ? 1 : 0.5,
                        }}
                        disabled={!internationalEnabled}
                        placeholder="20"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => {
                  const internationalEnabled = form.getFieldValue(
                    InternationalDescription,
                  );
                  return !internationalEnabled ? (
                    <Text type="secondary">
                      El margen de ganancia se aplica al precio final del viaje.
                    </Text>
                  ) : null;
                }}
              </Form.Item>
              {!form.getFieldValue(IdDescription) && (
                <Form.Item
                  name={PasswordDescription}
                  label="Contraseña"
                  tooltip="Cual es su contraseña?"
                  rules={[
                    {
                      required: true,
                      message: "Por favor ingrese una contraseña",
                    },
                    {
                      pattern: PasswordRegexExpression,
                      message: PasswordRequirementText,
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              )}
              <Space>
                <Button loading={isLoading} type="primary" htmlType="submit">
                  {form.getFieldValue(IdDescription)
                    ? AdminUpdate
                    : AdminCreate}
                </Button>
                <Button
                  disabled={isLoading}
                  onClick={() =>
                    navigate(WebRoutes.AdminTableRoute, {
                      replace: true,
                    })
                  }
                >
                  Cancelar
                </Button>
              </Space>
            </Col>
            {form.getFieldValue(PartnerDescription) && (
              <Col span={12}>
                <Form.Item>
                  <h1>Informacion del partner</h1>
                </Form.Item>
                {form.getFieldValue(PriceTableDescription) && (
                  <Row justify="center">
                    <Image
                      src={form.getFieldValue(PriceTableDescription)}
                      style={{ maxWidth: "300px", maxHeight: "200px" }}
                      alt="price table"
                      preview={false}
                    />
                  </Row>
                )}

                <Form.Item
                  label="Lista de precios"
                  valuePropName="file"
                  name="file"
                >
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>
                      Seleccionar imagen
                    </Button>
                  </Upload>
                </Form.Item>
                <Form.Item>
                  <Button
                    loading={isLoading}
                    type="primary"
                    onClick={onUpdatePriceTable}
                  >
                    Actualizar
                  </Button>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Col>
    </ContainerLayout>
  );
};

export default AdminForm;
